import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import SelectInput from '../../components/SelectInput';
import TextInput from '../../components/TextInput';
import { EwalletInfo } from '../../interfaces/ewallet-info';
import findEwallets from '../../utils/findEwallets';
import GRADES from '../../constants/grades';
import EwalletCard from './EwalletCard';
import { Pages } from '../../App';
import { ConfigContext } from '../../providers/ConfigContext';
import BigNumber from 'bignumber.js';

const SORTS: any[] = [
	{
		label: 'Recipient Name A-Z',
		value: 1,
	},
	{
		label: 'Recipient Name Z-A',
		value: 2,
	},
	{
		label: 'Teacher Name A-Z',
		value: 3,
	},
	{
		label: 'Teacher Name Z-A',
		value: 4,
	},
];

interface Props {
	isRefundPage: boolean;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setCurrentEWallet: React.Dispatch<React.SetStateAction<EwalletInfo>>;
}

const [retrieveEwallets] = findEwallets();

const EwalletDisplayContent = ({ isRefundPage, setCurrentPage, setCurrentEWallet }: Props) => {
	const { apiBase } = useContext(ConfigContext);

	const [sort, setSort] = useState('');
	const [grade, setGrade] = useState('');
	const [eWallets, setEwallets] = useState<EwalletInfo[]>([]);
	const [nameFilter, setNameFilter] = useState('');

	useEffect(() => {
		const getEwallets = async () => {
			const { data, error } = await retrieveEwallets(apiBase);
			if (error) {
				console.error(`Woops: ${error.message}`);
				setCurrentPage(Pages.Error);
			} else {
				for (const ewallet of data) {
					ewallet.amount = BigNumber(ewallet.amount);
				}
				setEwallets(data);
			}
		};

		getEwallets();
	}, [apiBase, setCurrentPage]);

	const sortedEwallets = [...eWallets].sort((a, b) => {
		const sortValue = SORTS.find((sortOption) => sortOption.label === sort)?.value || 1;
		const aLastName = sortValue < 3 ? a.studentLastName || a.teacherLastName : a.teacherLastName;
		const bLastName = sortValue < 3 ? b.studentLastName || b.teacherLastName : b.teacherLastName;

		if (sortValue % 2 === 1) {
			return aLastName.localeCompare(bLastName);
		} else {
			return bLastName.localeCompare(aLastName);
		}
	});

	// Filter the sorted eWallets array
	const filteredEwallets = sortedEwallets
		.filter((item) => {
			const matchingGrade = GRADES.find((item2) => item2.label === grade);
			return (matchingGrade && item.grade.toLowerCase() === matchingGrade.value.toLowerCase()) || grade === '' || grade === 'All Grades';
		})
		.filter((item) =>
			nameFilter === '' ? true : item.studentFirstName.concat(' ', item.studentLastName, ' ', item.teacherFirstName, ' ', item.teacherLastName).toLowerCase().includes(nameFilter.toLowerCase())
		);

	const handleClickAmount = (e: any, info: EwalletInfo) => {
		e.preventDefault();
		if (isRefundPage) {
			setCurrentEWallet(info);
			setCurrentPage(Pages.eWalletRefundTransactions);
			return;
		}
		setCurrentEWallet(info);
		setCurrentPage(Pages.eWalletReview);
	};

	// Grade change event handler
	const changeGrade = (value: string) => {
		window._satellite.track('ewallet-' + (isRefundPage ? 'refund' : 'payment') + '-grade-dropdown', { grade: value });
		setGrade(value);
	};

	// Sort change event handler
	const changeSort = (value: string) => {
		window._satellite.track('ewallet-' + (isRefundPage ? 'refund' : 'payment') + '-sort', { sort: value });
		setSort(value);
	};

	return (
		<>
			{isRefundPage ? (
				<TopBackNavBar setCurrentPage={setCurrentPage} title="eWallet Refund" location={Pages.ProcessRefund} />
			) : (
				<TopBackNavBar setCurrentPage={setCurrentPage} title="eWallet Payment" location={Pages.PaymentMethod} />
			)}
			<EwalletDisplayPage>
				<PageWrapper>
					<StyledTitle>Select an eWallet</StyledTitle>
					<FilterBar>
						<IndividualFilter>
							<TextInput
								label="Search Name"
								value={nameFilter}
								onChange={(value) => setNameFilter(value)}
								onBlur={() => {
									window._satellite.track('ewallet-payment-search', { searched: true });
								}}
							/>
						</IndividualFilter>
						<IndividualFilter>
							<SelectInput label={'Grade'} optionChosen={grade} onChange={changeGrade} options={GRADES.map((grade) => grade.label)} defaultText="Choose" />
						</IndividualFilter>
						<IndividualFilter>
							<SelectInput label={'Sort by'} optionChosen={sort} onChange={changeSort} options={SORTS.map((sortOption) => sortOption.label)} defaultText="Recipient Name A-Z" />
						</IndividualFilter>
					</FilterBar>
					<EwalletList>
						{filteredEwallets.map((item) => (
							<EwalletCard key={item.id} item={item} payment={true} handleClick={(e: any) => handleClickAmount(e, item)} />
						))}
					</EwalletList>
				</PageWrapper>
			</EwalletDisplayPage>
		</>
	);
};

const EwalletDisplayPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-bottom: 40px;
	width: 920px @media (max-width: 1280px) {
		width: 644px;
	}
	@media (max-width: 720px) {
		width: 280px;
	}
`;

const StyledTitle = styled.h1`
	font-size: 21px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
	text-align: center;
	margin: 21px 0 36px 0;
	width: 100%;
	@media (max-width: 720px) {
		text-align: left;
		margin-bottom: 20px;
	}
`;

const FilterBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-bottom: 32px;
	gap 40px;
    @media (max-width: 1280px) {
        gap: 28px;
        margin-bottom: 40px;
    } 
	@media (max-width: 720px) {
        gap: 20px;
        margin-bottom: 25px;
		flex-direction: column;
	}
`;

const IndividualFilter = styled.div`
	width: 280px;
	@media (max-width: 1280px) {
		width: 196px;
	}
	@media (max-width: 720px) {
		width: 280px;
	}
`;

const EwalletList = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: 24px 40px;
	width: 100%;
	@media (max-width: 1280px) {
		gap: 24px 28px;
	}
	@media (max-width: 720px) {
		gap: 16px;
	}
`;

export default EwalletDisplayContent;
